/**
 * Canvas Z-Order Management Utility
 * Ensures proper layering of canvas objects
 */

import type { Canvas as CanvasType, FabricObject } from '../lib/fabricLoader';

export interface ZOrderLayer {
  name: string;
  priority: number;
  matcher: (obj: FabricObject) => boolean;
}

// Define layers from bottom to top
export const Z_ORDER_LAYERS: ZOrderLayer[] = [
  {
    name: 'frame',
    priority: 0,
    matcher: (obj) => (obj as any).name === 'questionFrame'
  },
  {
    name: 'parentBackground',
    priority: 1,
    matcher: (obj) => (obj as any).name === 'parentQuestionBackground'
  },
  {
    name: 'questionBackground',
    priority: 2,
    matcher: (obj) => (obj as any).name === 'questionBackground'
  },
  {
    name: 'drawings',
    priority: 3,
    matcher: (obj) => {
      const objName = (obj as any).name || '';
      const isDrawing = obj.type === 'path' && (obj as any).strokeId;
      const isShape = ['rect', 'circle', 'ellipse', 'polygon', 'line'].includes(obj.type);
      return (isDrawing || isShape) && !objName.includes('question') && !objName.includes('optical');
    }
  },
  {
    name: 'handles',
    priority: 4,
    matcher: (obj) => (obj as any).name === 'questionResizeHandle' || (obj as any).name === 'questionMoveHandle'
  },
  {
    name: 'opticalChoices',
    priority: 5,
    matcher: (obj) => {
      const objName = (obj as any).name || '';
      return objName.startsWith('questionOpticalChoice_') || objName.startsWith('opticalChoice_');
    }
  }
];

/**
 * Enforces proper z-order on all canvas objects
 */
export function enforceZOrder(canvas: CanvasType): void {
  const objects = canvas.getObjects();
  
  // Group objects by layer
  const layers: Map<number, FabricObject[]> = new Map();
  
  objects.forEach(obj => {
    // Find which layer this object belongs to
    const layer = Z_ORDER_LAYERS.find(l => l.matcher(obj));
    if (layer) {
      if (!layers.has(layer.priority)) {
        layers.set(layer.priority, []);
      }
      layers.get(layer.priority)!.push(obj);
    }
  });
  
  // Sort layers by priority and reorder objects
  const sortedPriorities = Array.from(layers.keys()).sort((a, b) => a - b);
  
  // Move objects to their proper positions
  let currentIndex = 0;
  sortedPriorities.forEach(priority => {
    const layerObjects = layers.get(priority) || [];
    layerObjects.forEach(obj => {
      canvas.moveObjectTo(obj, currentIndex);
      currentIndex++;
    });
  });
}

/**
 * Ensures a specific object is placed in its correct z-order position
 */
export function placeObjectInCorrectLayer(canvas: CanvasType, obj: FabricObject): void {
  const layer = Z_ORDER_LAYERS.find(l => l.matcher(obj));
  if (!layer) return;
  
  const objects = canvas.getObjects();
  let targetIndex = 0;
  
  // Find the correct position for this object
  objects.forEach((canvasObj, index) => {
    if (canvasObj === obj) return; // Skip self
    
    const canvasLayer = Z_ORDER_LAYERS.find(l => l.matcher(canvasObj));
    if (canvasLayer && canvasLayer.priority < layer.priority) {
      targetIndex = index + 1;
    }
  });
  
  canvas.moveObjectTo(obj, targetIndex);
}

/**
 * Debug function to log current z-order
 */
export function logZOrder(canvas: CanvasType): void {
;
  canvas.getObjects().forEach((obj, idx) => {
    const layer = Z_ORDER_LAYERS.find(l => l.matcher(obj));
    const layerName = layer ? layer.name : 'unknown';
  });
}